/**
 * #1 補齊會員資料(2026-09-24,前身是入會表單 Onboarding.tsx)
 *
 * 一鍵加入的會員在結帳 / 送禮前才會被導來這裡。
 * 必填:姓名、手機;選填:Email、生日、寄送地址。
 * 送出 → POST /api/members/me/profile → 回 ?next= 指定的頁(預設 /home)。
 *
 * 兩種進入情境:
 *   1. 第一次補(還沒有手機)— 全欄位可填,送出後後端跑「手機 + 姓名」認親接舊客
 *   2. 已經有手機 — 姓名 / 手機鎖定(改了會誤觸認親;要改請找專屬顧問),
 *      其餘可改。姓名空著的例外:可以補一次(後端 complete_profile 本來就收),
 *      不然結帳 / 送禮被導來這裡會卡在「姓名鎖住又是空白」(2026-10-06)
 */
import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { completeProfile, getMe } from "../api/members";
import { useAuthStore } from "../store/authStore";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import { TAIWAN_CITIES, getDistricts } from "../data/tw-regions";
import BirthdaySelect from "../components/BirthdaySelect";
import { nextFromSearch, withNext } from "../utils/join";

// 後端 status → 中文文案(避免直接曝機器碼給客戶看)
const SUBMIT_ERR: Record<string, string> = {
  invalid: "請完整填寫姓名與手機",
  invalid_phone: "手機格式有誤,請輸入 09 開頭十位數",
  phone_taken: "此手機號碼已被其他會員使用,如需協助請聯繫專屬顧問",
};

export default function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const jwt = useAuthStore((s) => s.jwt);
  const next = nextFromSearch(location.search, "/home");

  // Auth guard:LIFF SDK 透過 liff.state 直接帶 user 到這頁時 Splash 不會跑,
  // authStore 沒 JWT → 提交時 401。沒 JWT 就先回 / 讓 Splash 做 liff-verify + 路由決策。
  useEffect(() => {
    if (!jwt) navigate("/", { replace: true });
  }, [jwt, navigate]);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressDistrict, setAddressDistrict] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [busy, setBusy] = useState(false);
  // 已經有手機 → 鎖定模式(姓名 / 手機不可改,其餘可改)
  const [locked, setLocked] = useState(false);
  // 鎖定模式下姓名只在「已經有填」時鎖 —— 空著的要能補,不然送不出去
  const [nameLocked, setNameLocked] = useState(false);

  // 載入既有資料 — memberStore 在 Splash 後只有部分欄位,從 getMe 撈完整 member 帶入。
  useEffect(() => {
    if (!jwt) return;
    (async () => {
      try {
        const m = await getMe();
        if (m.status !== "active") {
          // 還不是會員 → 先去一鍵加入,加完再回來補
          navigate(withNext("/join", `${location.pathname}${location.search}`), { replace: true });
          return;
        }
        if (m.name) setName(m.name);
        if (m.phone) {
          setPhone(m.phone);
          setLocked(true);
          setNameLocked(!!m.name?.trim());
        }
        if (m.email) setEmail(m.email);
        if (m.birthday) setBirthday(m.birthday);
        if (m.address?.city) setAddressCity(m.address.city);
        if (m.address?.district) setAddressDistrict(m.address.district);
        if (m.address?.detail) setAddressDetail(m.address.detail);
      } catch {
        // 撈不到就當第一次補,空表單照常 — 不打斷流程
      }
    })();
  }, [jwt, navigate, location.pathname, location.search]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const nameTrim = name.trim();
    const phoneTrim = phone.trim().replace(/[-\s]/g, "");
    const emailTrim = email.trim();

    if (!nameTrim || !phoneTrim) {
      useUIStore.getState().showToast(SUBMIT_ERR.invalid, "error");
      return;
    }
    // 台灣手機: 09 開頭十位數(去掉破折號後驗)
    if (!/^09\d{8}$/.test(phoneTrim)) {
      useUIStore.getState().showToast(SUBMIT_ERR.invalid_phone, "error");
      return;
    }
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      useUIStore.getState().showToast("Email 格式有誤,請確認後再送出", "error");
      return;
    }

    setBusy(true);
    try {
      const r = await completeProfile({
        name: nameTrim,
        phone: phoneTrim,
        email: emailTrim,
        birthday,
        address_city: addressCity.trim(),
        address_district: addressDistrict.trim(),
        address_detail: addressDetail.trim(),
      });
      if (r.status === "not_active") {
        navigate(withNext("/join", `${location.pathname}${location.search}`), { replace: true });
        return;
      }
      if (r.status !== "ok") {
        useUIStore.getState().showToast(SUBMIT_ERR[r.status] || "資料儲存未完成,請稍候再試", "error");
        return;
      }
      if (r.member) useMemberStore.getState().setMember(r.member);
      useUIStore.getState().showToast(locked ? "資料已更新" : "資料已補齊", "success");
      navigate(next, { replace: true });
    } catch {
      // network / 5xx — 不曝機器碼,給 user 雅緻提示
      useUIStore.getState().showToast("連線異常,請稍候再試", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-8">
        <div className="text-sm opacity-80">霽雲會員</div>
        <h1 className="text-2xl font-bold mt-1">{locked ? "修改資料" : "補齊會員資料"}</h1>
        <p className="text-sm mt-1 opacity-90">
          {locked ? "姓名與手機如需更正,請聯繫您的專屬顧問" : "結帳與送禮需要您的姓名與手機,僅蒐集必要資訊,依個資法保管"}
        </p>
      </header>

      <form onSubmit={submit} className="px-5 mt-5 space-y-4">
        <div>
          <label className="label" htmlFor="cp-name">姓名 *</label>
          <input
            id="cp-name"
            aria-label="姓名(必填)"
            className={`input ${nameLocked ? "bg-line/40 text-muted" : ""}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="您的真實姓名"
            required
            disabled={nameLocked}
          />
        </div>

        <div>
          <label className="label" htmlFor="cp-phone">手機 *</label>
          <input
            id="cp-phone"
            aria-label="手機號碼(必填)"
            className={`input ${locked ? "bg-line/40 text-muted" : ""}`}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0912-345-678"
            required
            disabled={locked}
          />
          {locked && (
            <p className="text-xs text-muted mt-1">姓名 / 手機如需更正,請聯繫您的專屬顧問協助</p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="cp-email">Email(選填)</label>
          <input
            id="cp-email"
            aria-label="電子郵件(選填)"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="label">生日(選填)</label>
          {/* 原 type="date" 對年長/高端客群不友善(只能點不能打、部分 iOS/LINE webview 不跳輪)
              且空值反灰像被鎖 → 改年/月/日三欄下拉,BirthdaySelect 對外仍吐 "YYYY-MM-DD" */}
          <BirthdaySelect value={birthday} onChange={setBirthday} />
        </div>

        <div>
          <label className="label" htmlFor="cp-city">寄送地址 — 縣市(選填)</label>
          <select
            id="cp-city"
            aria-label="寄送縣市(選填)"
            className="input"
            value={addressCity}
            onChange={(e) => {
              setAddressCity(e.target.value);
              setAddressDistrict("");  // 換縣市時 reset 區域,避免殘留前一個縣市的選項字
            }}
          >
            <option value="">請選擇縣市</option>
            {TAIWAN_CITIES.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="cp-district">區域(鄉鎮市區)</label>
          <select
            id="cp-district"
            aria-label="寄送區域(選填)"
            className="input"
            value={addressDistrict}
            onChange={(e) => setAddressDistrict(e.target.value)}
            disabled={!addressCity}
          >
            <option value="">{addressCity ? "請選擇區域" : "請先選擇縣市"}</option>
            {getDistricts(addressCity).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="cp-address-detail">詳細地址</label>
          <textarea
            id="cp-address-detail"
            aria-label="詳細地址(選填)"
            className="input resize-none"
            rows={2}
            value={addressDetail}
            onChange={(e) => setAddressDetail(e.target.value)}
            placeholder="路名、巷弄、號碼、樓層"
            disabled={!addressCity}
          />
        </div>

        <button type="submit" disabled={busy} className="btn-primary w-full mt-4">
          {busy ? "送出中…" : locked ? "儲存" : "儲存並繼續"}
        </button>

        <button
          type="button"
          onClick={() => navigate("/home", { replace: true })}
          className="w-full text-center text-sm text-muted underline underline-offset-4 py-2"
        >
          先不補,回首頁
        </button>
      </form>
    </div>
  );
}
